import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from "idb";

import {
  trimDatabaseWhitespace,
  type ContributionTarget,
  type OAuthProvider,
} from "../shared/api-contract";

export type PendingIntentStatus =
  | "confirmed"
  | "oauth_pending"
  | "completing"
  | "retryable_error"
  | "expired";

export interface PendingContribution {
  intentId: string;
  idempotencyKey: string;
  target: ContributionTarget;
  createdAt: number;
  expiresAt: number;
  status: PendingIntentStatus;
  provider?: OAuthProvider;
  errorCode?: string;
}

export interface OAuthAttemptLease {
  key: "active";
  attemptId: string;
  intentId: string;
  provider: OAuthProvider;
  ownerId: string;
  acquiredAt: number;
  expiresAt: number;
  status: "active" | "exchanging";
}

export interface SubmissionLease {
  intentId: string;
  ownerId: string;
  acquiredAt: number;
  expiresAt: number;
}

export interface SubmissionLeaseRenewal {
  stop(): Promise<void>;
}

interface PendingContributionDatabase extends DBSchema {
  intents: {
    key: string;
    value: PendingContribution;
  };
  leases: {
    key: "active";
    value: OAuthAttemptLease;
  };
  submissions: {
    key: string;
    value: SubmissionLease;
  };
}

export class PendingIntentConflictError extends Error {
  constructor() {
    super("An unfinished contribution already exists. Cancel it before choosing another.");
    this.name = "PendingIntentConflictError";
  }
}

export class OAuthLeaseConflictError extends Error {
  constructor() {
    super("A sign-in attempt is already active. Cancel it before starting another.");
    this.name = "OAuthLeaseConflictError";
  }
}

export class PendingIntentExpiredError extends Error {
  constructor() {
    super("The confirmed contribution has expired.");
    this.name = "PendingIntentExpiredError";
  }
}

export class OAuthCallbackMismatchError extends Error {
  constructor(message = "The sign-in callback does not match the active attempt.") {
    super(message);
    this.name = "OAuthCallbackMismatchError";
  }
}

export interface PendingContributionStoreOptions {
  databaseName?: string;
  now?: () => number;
  intentTtlMs?: number;
  oauthLeaseTtlMs?: number;
  submissionLeaseTtlMs?: number;
}

const DEFAULT_DATABASE_NAME = "the-shit-list.pending-contribution.v1";
const DEFAULT_INTENT_TTL_MS = 30 * 60 * 1_000;
const DEFAULT_OAUTH_LEASE_TTL_MS = 10 * 60 * 1_000;
const DEFAULT_SUBMISSION_LEASE_TTL_MS = 15 * 1_000;

export class PendingContributionStore {
  readonly #databaseName: string;
  readonly #now: () => number;
  readonly #intentTtlMs: number;
  readonly #oauthLeaseTtlMs: number;
  readonly #submissionLeaseTtlMs: number;
  readonly #database: Promise<IDBPDatabase<PendingContributionDatabase>>;

  constructor(options: PendingContributionStoreOptions = {}) {
    this.#databaseName = options.databaseName ?? DEFAULT_DATABASE_NAME;
    this.#now = options.now ?? Date.now;
    this.#intentTtlMs = options.intentTtlMs ?? DEFAULT_INTENT_TTL_MS;
    this.#oauthLeaseTtlMs = options.oauthLeaseTtlMs ?? DEFAULT_OAUTH_LEASE_TTL_MS;
    this.#submissionLeaseTtlMs = options.submissionLeaseTtlMs ?? DEFAULT_SUBMISSION_LEASE_TTL_MS;
    this.#database = openDB<PendingContributionDatabase>(this.#databaseName, 2, {
      upgrade(database, oldVersion) {
        if (oldVersion < 1) {
          database.createObjectStore("intents", { keyPath: "intentId" });
          database.createObjectStore("leases", { keyPath: "key" });
        }
        if (oldVersion < 2) {
          database.createObjectStore("submissions", { keyPath: "intentId" });
        }
      },
    });
  }

  async create(target: ContributionTarget): Promise<PendingContribution> {
    const normalizedTarget = normalizeTarget(target);
    const now = this.#now();
    const database = await this.#database;
    const transaction = database.transaction("intents", "readwrite");
    const intents = await transaction.store.getAll();
    const unfinished = intents.find((intent) => intent.status !== "expired" && intent.expiresAt > now);
    await Promise.all(
      intents
        .filter((intent) => intent !== unfinished)
        .map((intent) => transaction.store.delete(intent.intentId)),
    );
    if (unfinished) {
      await transaction.done;
      throw new PendingIntentConflictError();
    }

    const intent: PendingContribution = {
      intentId: crypto.randomUUID(),
      idempotencyKey: crypto.randomUUID(),
      target: normalizedTarget,
      createdAt: now,
      expiresAt: now + this.#intentTtlMs,
      status: "confirmed",
    };
    await transaction.store.put(intent);
    await transaction.done;
    return intent;
  }

  async get(intentId: string): Promise<PendingContribution | undefined> {
    return (await this.#database).get("intents", intentId);
  }

  async getUnfinished(): Promise<PendingContribution | undefined> {
    const now = this.#now();
    const database = await this.#database;
    const transaction = database.transaction("intents", "readwrite");
    const intents = await transaction.store.getAll();
    const sorted = intents.sort((left, right) => right.createdAt - left.createdAt);
    const intent = sorted.find(
      (candidate) => candidate.status !== "expired" && candidate.expiresAt > now,
    );
    if (!intent) {
      await Promise.all(sorted.map((candidate) => transaction.store.delete(candidate.intentId)));
      await transaction.done;
      return undefined;
    }
    await Promise.all(
      sorted
        .filter((candidate) => candidate.intentId !== intent.intentId)
        .map((candidate) => transaction.store.delete(candidate.intentId)),
    );
    await transaction.done;
    return intent;
  }

  async acquireOAuthLease(
    intentId: string,
    provider: OAuthProvider,
    ownerId: string,
  ): Promise<OAuthAttemptLease> {
    const now = this.#now();
    const database = await this.#database;
    const transaction = database.transaction(["intents", "leases"], "readwrite");
    const intentStore = transaction.objectStore("intents");
    const leaseStore = transaction.objectStore("leases");
    const intent = await intentStore.get(intentId);
    if (!intent) {
      await transaction.done;
      throw new OAuthCallbackMismatchError("The confirmed contribution is missing.");
    }
    if (intent.expiresAt <= now || intent.status === "expired") {
      intent.status = "expired";
      await intentStore.put(intent);
      await transaction.done;
      throw new PendingIntentExpiredError();
    }
    if (intent.status !== "confirmed" && intent.status !== "retryable_error") {
      await transaction.done;
      throw new OAuthLeaseConflictError();
    }

    const activeLease = await leaseStore.get("active");
    if (activeLease && activeLease.expiresAt > now) {
      await transaction.done;
      throw new OAuthLeaseConflictError();
    }
    if (activeLease) await leaseStore.delete("active");

    const lease: OAuthAttemptLease = {
      key: "active",
      attemptId: crypto.randomUUID(),
      intentId,
      provider,
      ownerId,
      acquiredAt: now,
      expiresAt: now + this.#oauthLeaseTtlMs,
      status: "active",
    };
    intent.status = "oauth_pending";
    intent.provider = provider;
    delete intent.errorCode;
    await intentStore.put(intent);
    await leaseStore.put(lease);
    await transaction.done;
    return lease;
  }

  async getActiveLease(): Promise<OAuthAttemptLease | undefined> {
    const lease = await (await this.#database).get("leases", "active");
    return lease && lease.expiresAt > this.#now() ? lease : undefined;
  }

  async beginOAuthCallback(
    attemptId: string,
  ): Promise<{ intent: PendingContribution; lease: OAuthAttemptLease }> {
    const now = this.#now();
    const database = await this.#database;
    const transaction = database.transaction(["intents", "leases"], "readwrite");
    const intentStore = transaction.objectStore("intents");
    const leaseStore = transaction.objectStore("leases");
    const lease = await leaseStore.get("active");

    if (!lease || lease.attemptId !== attemptId) {
      await transaction.done;
      throw new OAuthCallbackMismatchError("The sign-in callback does not match the active attempt.");
    }
    if (lease.status !== "active") {
      await transaction.done;
      throw new OAuthCallbackMismatchError("This sign-in callback is already being handled.");
    }

    const intent = await intentStore.get(lease.intentId);
    if (!intent) {
      await leaseStore.delete("active");
      await transaction.done;
      throw new OAuthCallbackMismatchError("The confirmed contribution is missing.");
    }
    if (intent.expiresAt <= now || lease.expiresAt <= now) {
      intent.status = "expired";
      delete intent.errorCode;
      await intentStore.put(intent);
      await leaseStore.delete("active");
      await transaction.done;
      throw new PendingIntentExpiredError();
    }

    lease.status = "exchanging";
    await leaseStore.put(lease);
    await transaction.done;
    return { intent, lease };
  }

  async markCompleting(intentId: string, attemptId: string): Promise<PendingContribution> {
    const database = await this.#database;
    const transaction = database.transaction(["intents", "leases"], "readwrite");
    const intentStore = transaction.objectStore("intents");
    const leaseStore = transaction.objectStore("leases");
    const [intent, lease] = await Promise.all([
      intentStore.get(intentId),
      leaseStore.get("active"),
    ]);
    if (
      !intent ||
      !lease ||
      lease.intentId !== intentId ||
      lease.attemptId !== attemptId ||
      lease.status !== "exchanging"
    ) {
      await transaction.done;
      throw new OAuthCallbackMismatchError();
    }
    intent.status = "completing";
    delete intent.errorCode;
    await intentStore.put(intent);
    await leaseStore.delete("active");
    await transaction.done;
    return intent;
  }

  async markRetryable(intentId: string, errorCode: string): Promise<void> {
    const database = await this.#database;
    const transaction = database.transaction(["intents", "leases"], "readwrite");
    const intentStore = transaction.objectStore("intents");
    const leaseStore = transaction.objectStore("leases");
    const intent = await intentStore.get(intentId);
    if (intent && intent.status !== "expired") {
      intent.status = "retryable_error";
      intent.errorCode = errorCode;
      await intentStore.put(intent);
    }
    const lease = await leaseStore.get("active");
    if (lease?.intentId === intentId) await leaseStore.delete("active");
    await transaction.done;
  }

  async markCompletingWithoutOAuth(intentId: string): Promise<PendingContribution> {
    const database = await this.#database;
    const transaction = database.transaction("intents", "readwrite");
    const intent = await transaction.store.get(intentId);
    if (!intent || intent.status !== "confirmed") {
      await transaction.done;
      throw new OAuthCallbackMismatchError("The confirmed contribution is not ready.");
    }
    if (intent.expiresAt <= this.#now()) {
      intent.status = "expired";
      await transaction.store.put(intent);
      await transaction.done;
      throw new PendingIntentExpiredError();
    }
    intent.status = "completing";
    await transaction.store.put(intent);
    await transaction.done;
    return intent;
  }

  async acquireSubmissionLease(
    intentId: string,
    ownerId: string,
  ): Promise<{ acquired: boolean; retryAfterMs: number }> {
    const now = this.#now();
    const database = await this.#database;
    const transaction = database.transaction(["intents", "submissions"], "readwrite");
    const intent = await transaction.objectStore("intents").get(intentId);
    if (!intent || (intent.status !== "completing" && intent.status !== "retryable_error")) {
      await transaction.done;
      throw new OAuthCallbackMismatchError("The confirmed contribution is not ready.");
    }
    if (intent.expiresAt <= now) {
      intent.status = "expired";
      await transaction.objectStore("intents").put(intent);
      await transaction.done;
      throw new PendingIntentExpiredError();
    }

    const store = transaction.objectStore("submissions");
    const active = await store.get(intentId);
    if (active && active.expiresAt > now) {
      await transaction.done;
      return { acquired: false, retryAfterMs: active.expiresAt - now };
    }
    const lease: SubmissionLease = {
      intentId,
      ownerId,
      acquiredAt: now,
      expiresAt: now + this.#submissionLeaseTtlMs,
    };
    await store.put(lease);
    await transaction.done;
    return { acquired: true, retryAfterMs: 0 };
  }

  async renewSubmissionLease(intentId: string, ownerId: string): Promise<boolean> {
    const now = this.#now();
    const database = await this.#database;
    const transaction = database.transaction("submissions", "readwrite");
    const lease = await transaction.store.get(intentId);
    if (!lease || lease.ownerId !== ownerId || lease.expiresAt <= now) {
      await transaction.done;
      return false;
    }
    lease.expiresAt = now + this.#submissionLeaseTtlMs;
    await transaction.store.put(lease);
    await transaction.done;
    return true;
  }

  startSubmissionLeaseRenewal(
    intentId: string,
    ownerId: string,
  ): SubmissionLeaseRenewal {
    const intervalMs = Math.max(1, Math.floor(this.#submissionLeaseTtlMs / 3));
    let timer: ReturnType<typeof globalThis.setInterval> | undefined;
    let stopped = false;
    let inFlight: Promise<void> | null = null;
    let failure: unknown;

    const stopTimer = () => {
      stopped = true;
      if (timer === undefined) return;
      globalThis.clearInterval(timer);
      timer = undefined;
    };
    const renew = () => {
      if (stopped || inFlight) return;
      inFlight = this.renewSubmissionLease(intentId, ownerId)
        .then((renewed) => {
          if (!renewed) {
            throw new OAuthCallbackMismatchError(
              "The contribution submission lease could not be renewed.",
            );
          }
        })
        .catch((error: unknown) => {
          failure = error;
          stopTimer();
        })
        .finally(() => {
          inFlight = null;
        });
    };

    timer = globalThis.setInterval(renew, intervalMs);
    return {
      stop: async () => {
        stopTimer();
        await inFlight;
        if (failure) throw failure;
      },
    };
  }

  async releaseSubmissionLease(intentId: string, ownerId: string): Promise<void> {
    const database = await this.#database;
    const transaction = database.transaction("submissions", "readwrite");
    const lease = await transaction.store.get(intentId);
    if (lease?.ownerId === ownerId) await transaction.store.delete(intentId);
    await transaction.done;
  }

  async complete(intentId: string, ownerId: string): Promise<void> {
    const database = await this.#database;
    const transaction = database.transaction(["intents", "submissions"], "readwrite");
    const lease = await transaction.objectStore("submissions").get(intentId);
    if (!lease || lease.ownerId !== ownerId) {
      await transaction.done;
      throw new OAuthCallbackMismatchError("The contribution submission is owned by another page.");
    }
    await Promise.all([
      transaction.objectStore("intents").delete(intentId),
      transaction.objectStore("submissions").delete(intentId),
    ]);
    await transaction.done;
  }

  async cancel(intentId: string): Promise<void> {
    const database = await this.#database;
    const transaction = database.transaction(["intents", "leases", "submissions"], "readwrite");
    const leaseStore = transaction.objectStore("leases");
    const lease = await leaseStore.get("active");
    if (lease?.intentId === intentId) await leaseStore.delete("active");
    await Promise.all([
      transaction.objectStore("intents").delete(intentId),
      transaction.objectStore("submissions").delete(intentId),
    ]);
    await transaction.done;
  }

  async clearUnfinished(): Promise<void> {
    const database = await this.#database;
    const transaction = database.transaction(["intents", "leases", "submissions"], "readwrite");
    await Promise.all([
      transaction.objectStore("intents").clear(),
      transaction.objectStore("leases").clear(),
      transaction.objectStore("submissions").clear(),
    ]);
    await transaction.done;
  }

  async destroyForTests(): Promise<void> {
    const database = await this.#database;
    database.close();
    await deleteDB(this.#databaseName);
  }
}

function normalizeTarget(target: ContributionTarget): ContributionTarget {
  const name = trimDatabaseWhitespace(target.name);
  if (!name || name.length > 80) throw new Error("Contribution names must be 1 to 80 characters.");
  if (target.kind === "created") {
    if (!(target.image instanceof Blob) || target.image.size === 0) {
      throw new Error("A created contribution requires an image.");
    }
    return { kind: "created", name, image: target.image };
  }
  if (!target.itemId) throw new Error("An existing contribution requires an item ID.");
  return { ...target, name };
}
